import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PrescriptionForm from "./PrescriptionForm";
import PrescriptorEntriesList from "./PrescriptorEntriesList";
import {
  CONDITION_ACTION_OPTIONS,
  CONDITION_OPTIONS,
  createBlock,
  createDosageLine,
  createId,
  createLineByType,
  DOSE_OPTIONS,
  DURATION_OPTIONS,
  findBlock,
  flattenBlocks,
  FREQUENCY_OPTIONS,
  lineLabel,
  mapBlocks,
  MEAL_OPTIONS,
  MOMENT_OPTIONS,
  QTY_OPTIONS,
  RENEWAL_OPTIONS,
  ROUTE_OPTIONS,
  UNIT_OPTIONS,
} from "./prescriptorData";
import { searchMedicinesDetailsApi } from "../../../services/apiService";
import { useCategoriesWithTests } from "../../../queries";
import "./NewPrescriptorTab.css";

const cloneEntryData = (entry) => {
  if (!entry) return null;
  return JSON.parse(JSON.stringify(entry));
};

const createLocalDraftEntry = (index) => ({
  id: createId("entry"),
  title: `Entry ${index}`,
  note: "",
  isDraft: true,
  blocks: [createBlock("")],
});

const normalizeIntegerString = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const parsed = Number.parseInt(String(value), 10);
  if (Number.isNaN(parsed)) return "";
  return String(parsed);
};

const sanitizeLine = (line) => {
  const lineType = line?.type || line?.line_type || "dosage_line";
  if (lineType === "condition_line") {
    return {
      id: String(line?.id || createId("line")),
      type: "condition_line",
      expression: line?.expression || "",
      action_type: line?.action_type || "none",
      add_line_payload: line?.add_line_payload || createDosageLine(),
      action_medication: line?.action_medication || "",
      spawned_block_id: line?.spawned_block_id
        ? String(line.spawned_block_id)
        : "",
    };
  }

  if (lineType === "test_line") {
    return {
      id: String(line?.id || createId("line")),
      type: "test_line",
      tests: Array.isArray(line?.tests)
        ? line.tests.map((test) => ({
            id: String(test?.id || test?.medical_test_id || createId("test")),
            medical_test_id: test?.medical_test_id || test?.id || null,
            name: test?.name || "",
            group: test?.group || test?.group_name || "",
            category_id: test?.category_id || null,
            notes: test?.notes || "",
          }))
        : [],
    };
  }

  return {
    id: String(line?.id || createId("line")),
    type: lineType,
    dose: line?.dose || "",
    unite: line?.unite || line?.unit || "",
    frequence: line?.frequence || line?.frequency || "",
    voie: line?.voie || line?.route || "",
    moment: line?.moment || "",
    repas: line?.repas || line?.meal || "",
    duree_valeur: line?.duree_valeur || line?.duration_value || "",
    duree_unite: line?.duree_unite || line?.duration_unit || "",
    duree_calendar_start:
      line?.duree_calendar_start !== null &&
      line?.duree_calendar_start !== undefined
        ? String(line.duree_calendar_start)
        : "",
    duree_calendar_end:
      line?.duree_calendar_end !== null &&
      line?.duree_calendar_end !== undefined
        ? String(line.duree_calendar_end)
        : "",
    condition: line?.condition || line?.condition_text || "",
    renouvellement: line?.renouvellement || line?.renewal || "",
    notes: line?.notes || "",
  };
};

const sanitizeBlock = (block) => {
  const nextLines = Array.isArray(block?.lines)
    ? block.lines.map(sanitizeLine)
    : [createDosageLine()];
  return {
    id: String(block?.id || createId("block")),
    medicament: {
      name: block?.medicament?.name || block?.medication_name || "",
      shortName: block?.medicament?.shortName || block?.meta_json?.short_name || "",
    },
    quantite: normalizeIntegerString(block?.quantite ?? block?.quantity),
    type_quantite: block?.type_quantite || block?.quantity_unit || "",
    lines: nextLines.length ? nextLines : [createDosageLine()],
    children: Array.isArray(block?.children)
      ? block.children.map(sanitizeBlock)
      : [],
  };
};

const sanitizeEntry = (entry, index) => {
  const blocks =
    Array.isArray(entry?.blocks) && entry.blocks.length
      ? entry.blocks.map(sanitizeBlock)
      : [createBlock("")];
  return {
    id: String(entry?.id || createId("entry")),
    title: entry?.title || `Entry ${index + 1}`,
    note: entry?.note || "",
    isDraft: Boolean(entry?.isDraft),
    blocks,
  };
};

const textFromHtml = (html) => {
  if (!html) return "";
  const wrapper = document.createElement("div");
  wrapper.innerHTML = html;
  return (wrapper.textContent || wrapper.innerText || "").trim();
};

const parseProtocolTemplate = (raw) => {
  const content = String(raw || "").trim();
  if (!content) return [];

  try {
    const parsed = JSON.parse(content);

    if (Array.isArray(parsed)) {
      return parsed.map((entry, index) => ({
        ...sanitizeEntry(entry, index),
        isDraft: false,
      }));
    }

    if (Array.isArray(parsed?.entries)) {
      return parsed.entries.map((entry, index) => ({
        ...sanitizeEntry(entry, index),
        isDraft: false,
      }));
    }

    const prescriptionEntries = parsed?.prescriptions?.[0]?.entries;
    if (Array.isArray(prescriptionEntries)) {
      return prescriptionEntries.map((entry, index) => ({
        ...sanitizeEntry(entry, index),
        isDraft: false,
      }));
    }
  } catch (error) {
    const note = textFromHtml(content);
    if (!note) return [];
    return [
      {
        ...createLocalDraftEntry(1),
        isDraft: false,
        note,
      },
    ];
  }

  return [];
};

const hasLineContent = (line) => {
  if (!line) return false;
  if (line.type === "test_line")
    return Array.isArray(line.tests) && line.tests.length > 0;
  if (line.type === "condition_line") {
    return Boolean(
      String(line.expression || "").trim() ||
      String(line.action_medication || "").trim() ||
      line.action_type === "add_dosage_line",
    );
  }

  return Boolean(
    String(line.dose || "").trim() ||
    String(line.unite || "").trim() ||
    String(line.frequence || "").trim() ||
    String(line.voie || "").trim() ||
    String(line.moment || "").trim() ||
    String(line.repas || "").trim() ||
    String(line.duree_valeur || "").trim() ||
    String(line.duree_unite || "").trim() ||
    String(line.duree_calendar_start || "").trim() ||
    String(line.duree_calendar_end || "").trim() ||
    String(line.condition || "").trim() ||
    String(line.renouvellement || "").trim(),
  );
};

const hasBlockContent = (block) => {
  if (!block) return false;
  if (String(block?.medicament?.name || "").trim()) return true;
  if (String(block?.quantite || "").trim()) return true;
  if (String(block?.type_quantite || "").trim()) return true;
  if ((block?.lines || []).some(hasLineContent)) return true;
  return (block?.children || []).some(hasBlockContent);
};

const hasEntryContent = (entry) => {
  if (!entry) return false;
  if (String(entry.note || "").trim()) return true;
  return (entry.blocks || []).some(hasBlockContent);
};

const serializeProtocolTemplate = (entries) => {
  const normalizedEntries = (entries || [])
    .filter(hasEntryContent)
    .map((entry, index) => ({
      ...cloneEntryData(entry),
      id: String(entry.id || createId("entry")),
      title: String(entry.title || `Entry ${index + 1}`),
      isDraft: false,
      blocks: (entry.blocks || []).map((block) => ({
        ...cloneEntryData(block),
        quantite: normalizeIntegerString(block?.quantite),
      })),
    }));

  if (!normalizedEntries.length) return "";

  return JSON.stringify({
    format: "prescriptor_protocol_v1",
    version: 1,
    entries: normalizedEntries,
  });
};

const buildEditorEntries = (initialTemplate) => {
  const loadedEntries = parseProtocolTemplate(initialTemplate);
  if (!loadedEntries.length) return [createLocalDraftEntry(1)];

  const sanitized = loadedEntries.map((entry, index) => ({
    ...sanitizeEntry(entry, index),
    isDraft: false,
  }));
  return [...sanitized, createLocalDraftEntry(sanitized.length + 1)];
};

const ProtocolPrescriptorComposer = ({
  initialTemplate = "",
  onTemplateChange,
}) => {
  const initialEntries = useMemo(
    () => buildEditorEntries(initialTemplate),
    [initialTemplate],
  );
  const initialSelectedEntry =
    initialEntries.find((entry) => entry.isDraft) || initialEntries[0] || null;
  const initialSelectedBlock = initialSelectedEntry?.blocks?.[0] || null;

  const [entries, setEntries] = useState(initialEntries);
  const [selectedEntryId, setSelectedEntryId] = useState(
    initialSelectedEntry?.id || null,
  );
  const [selectedBlockId, setSelectedBlockId] = useState(
    initialSelectedBlock?.id || null,
  );
  const [lineWizardIndex, setLineWizardIndex] = useState(
    initialSelectedBlock?.id ? { [initialSelectedBlock.id]: 0 } : {},
  );
  const [savedMessage, setSavedMessage] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [medicationOptions, setMedicationOptions] = useState([]);
  const { data: rawCategoriesWithTests = [] } = useCategoriesWithTests();

  const testGroups = useMemo(() => {
    const categories = Array.isArray(rawCategoriesWithTests)
      ? rawCategoriesWithTests
      : [];

    return categories.map((category) => ({
      id: String(category.id),
      name: category.name || "Unnamed category",
      items: (category.medical_tests || []).map((test) => ({
        id: String(test.id),
        name: test.name || test.abrv || `Test ${test.id}`,
        value: {
          id: String(test.id),
          name: test.name || test.abrv || `Test ${test.id}`,
          group: category.name || "",
        },
      })),
    }));
  }, [rawCategoriesWithTests]);
  const medicationSearchReqRef = useRef(0);
  const entryInitialStateRef = useRef({});

  const selectedEntry =
    entries.find((entry) => entry.id === selectedEntryId) || null;
  const selectedEntryIndex = entries.findIndex(
    (entry) => entry.id === selectedEntryId,
  );
  const blockTree = selectedEntry?.blocks || [];
  const selectedBlock = findBlock(blockTree, selectedBlockId) || null;
  const selectedLineIndex = lineWizardIndex[selectedBlock?.id] || 0;
  const activeLine = selectedBlock?.lines?.[selectedLineIndex] || null;

  const blockFlatList = useMemo(() => flattenBlocks(blockTree), [blockTree]);

  const duplicateWarnings = useMemo(() => {
    const names = blockFlatList
      .map((item) => (item.block.medicament?.name || "").trim().toLowerCase())
      .filter(Boolean);

    const counts = names.reduce((acc, name) => {
      acc[name] = (acc[name] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(counts)
      .filter(([, count]) => count > 1)
      .map(([name, count]) => `${name} appears ${count} times.`);
  }, [blockFlatList]);

  const seedEntryInitialStates = useCallback((entryList) => {
    const nextSnapshots = { ...entryInitialStateRef.current };
    (entryList || []).forEach((entry) => {
      if (!entry?.id || entry.isDraft) return;
      if (nextSnapshots[entry.id]) return;
      nextSnapshots[entry.id] = cloneEntryData(entry);
    });
    entryInitialStateRef.current = nextSnapshots;
  }, []);

  useEffect(() => {
    seedEntryInitialStates(entries);
  }, [entries, seedEntryInitialStates]);

  useEffect(() => {
    if (!entries.length) return;

    const hasSelectedEntry = entries.some(
      (entry) => entry.id === selectedEntryId,
    );
    if (!hasSelectedEntry) {
      const fallbackEntry =
        entries.find((entry) => entry.isDraft) || entries[0];
      const fallbackBlock = fallbackEntry?.blocks?.[0] || null;
      setSelectedEntryId(fallbackEntry?.id || null);
      setSelectedBlockId(fallbackBlock?.id || null);
      if (fallbackBlock?.id) {
        setLineWizardIndex((prev) => ({ ...prev, [fallbackBlock.id]: 0 }));
      }
      return;
    }

    if (!selectedBlockId && selectedEntry?.blocks?.[0]?.id) {
      const fallbackBlockId = selectedEntry.blocks[0].id;
      setSelectedBlockId(fallbackBlockId);
      setLineWizardIndex((prev) => ({ ...prev, [fallbackBlockId]: 0 }));
      return;
    }

    if (selectedBlockId && selectedEntry?.blocks?.length) {
      const nextSelectedBlock = findBlock(
        selectedEntry.blocks,
        selectedBlockId,
      );
      if (!nextSelectedBlock) {
        const fallbackBlockId = selectedEntry.blocks[0].id;
        setSelectedBlockId(fallbackBlockId);
        setLineWizardIndex((prev) => ({ ...prev, [fallbackBlockId]: 0 }));
      }
    }
  }, [entries, selectedBlockId, selectedEntry, selectedEntryId]);

  useEffect(() => {
    onTemplateChange?.(serializeProtocolTemplate(entries));
  }, [entries, onTemplateChange]);



  const handleMedicationQuery = useCallback(async (query) => {
    const term = String(query || "").trim();
    const requestId = ++medicationSearchReqRef.current;

    if (!term) {
      setMedicationOptions([]);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("search", term);
      const response = await searchMedicinesDetailsApi(formData);

      if (requestId !== medicationSearchReqRef.current) return;

      const rows =
        (response?.data?.res === true && Array.isArray(response?.data?.list)
          ? response.data.list
          : null) ||
        (Array.isArray(response?.data?.data?.list)
          ? response.data.data.list
          : null) ||
        [];

      const mapped = rows.map((item) => {
        const commercialName =
          item?.name || item?.brand || item?.denomination || "";
        const medicineForm = item?.forms || item?.form || "";
        const medicineDosage = item?.dosage || "";
        const medicinePackaging =
          item?.conditionnement || item?.packaging || "";
        const labelParts = [
          commercialName,
          medicineForm,
          medicineDosage,
          medicinePackaging,
        ].filter(Boolean);
        const label =
          labelParts.join(" - ") || commercialName || "Unknown medication";
        const value =
          [commercialName, medicineDosage].filter(Boolean).join(" ") || label;
        return {
          value,
          label,
          badge: item?.medicine ? "PROTO" : "MED",
          meta: item?.dosage || "",
        };
      });

      setMedicationOptions(mapped);
    } catch (error) {
      if (requestId === medicationSearchReqRef.current) {
        setMedicationOptions([]);
      }
    }
  }, []);

  const updateEntryBlocks = (entryId, updater) => {
    setEntries((prev) =>
      prev.map((entry) => {
        if (entry.id !== entryId) return entry;
        return { ...entry, blocks: updater(entry.blocks || []) };
      }),
    );
  };

  const updateSelectedBlock = (updater) => {
    if (!selectedEntry || !selectedBlock) return;
    updateEntryBlocks(selectedEntry.id, (blocks) =>
      mapBlocks(blocks, (block) => {
        if (block.id !== selectedBlock.id) return block;
        const updatedBlock = updater(block);
        return {
          ...updatedBlock,
          quantite: normalizeIntegerString(updatedBlock?.quantite),
        };
      }),
    );
  };

  const updateLine = (lineId, updater) => {
    updateSelectedBlock((block) => {
      let nextChildren = block.children || [];
      const nextLines = (block.lines || []).map((line) => {
        if (line.id !== lineId) return line;
        const updated = updater(line);

        if (line.type === "condition_line" && line.spawned_block_id) {
          const actionChanged =
            updated.action_type !== line.action_type ||
            updated.action_medication !== line.action_medication;
          const actionNoSpawn =
            updated.action_type === "none" ||
            updated.action_type === "add_dosage_line";
          if (actionChanged || actionNoSpawn) {
            nextChildren = nextChildren.filter(
              (child) => child.id !== line.spawned_block_id,
            );
            return { ...updated, spawned_block_id: "" };
          }
        }

        return updated;
      });

      return { ...block, lines: nextLines, children: nextChildren };
    });
  };

  const addEntry = () => {
    const newEntry = createLocalDraftEntry(entries.length + 1);
    setEntries((prev) => [...prev, newEntry]);
    setSelectedEntryId(newEntry.id);
    setSelectedBlockId(newEntry.blocks[0].id);
    setLineWizardIndex((prev) => ({ ...prev, [newEntry.blocks[0].id]: 0 }));
  };

  const selectEntry = (entryId) => {
    const nextEntry =
      entries.find((entry) => entry.id === entryId) || entries[0];
    if (!nextEntry) return;

    if (
      !nextEntry.isDraft &&
      nextEntry.id &&
      !entryInitialStateRef.current[nextEntry.id]
    ) {
      entryInitialStateRef.current[nextEntry.id] = cloneEntryData(nextEntry);
    }

    const nextBlock = nextEntry.blocks?.[0] || null;
    setSelectedEntryId(nextEntry.id);
    setSelectedBlockId(nextBlock?.id || null);
    if (nextBlock?.id) {
      setLineWizardIndex((prev) => ({ ...prev, [nextBlock.id]: 0 }));
    }
  };

  const deleteEntry = (entryId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this entry?",
    );
    if (!confirmDelete) return;

    const nextEntries = entries.filter((entry) => entry.id !== entryId);

    if (!nextEntries.length) {
      const fallbackEntry = createLocalDraftEntry(1);
      setEntries([fallbackEntry]);
      setSelectedEntryId(fallbackEntry.id);
      setSelectedBlockId(fallbackEntry.blocks[0].id);
      setLineWizardIndex((prev) => ({
        ...prev,
        [fallbackEntry.blocks[0].id]: 0,
      }));
      return;
    }

    setEntries(nextEntries);

    const keepSelected = selectedEntryId !== entryId;
    const nextSelectedEntry = keepSelected
      ? nextEntries.find((entry) => entry.id === selectedEntryId) ||
        nextEntries[0]
      : nextEntries[0];
    const nextBlock = nextSelectedEntry.blocks?.[0] || null;

    setSelectedEntryId(nextSelectedEntry?.id || null);
    setSelectedBlockId(nextBlock?.id || null);
    if (nextBlock?.id) {
      setLineWizardIndex((prev) => ({ ...prev, [nextBlock.id]: 0 }));
    }
  };

  const addLine = (type = "dosage_line") => {
    if (!selectedBlock) return;
    const line = createLineByType(type);
    updateSelectedBlock((block) => ({
      ...block,
      lines: [...(block.lines || []), line],
    }));
    setLineWizardIndex((prev) => ({
      ...prev,
      [selectedBlock.id]: (selectedBlock.lines || []).length,
    }));
  };

  const removeCurrentLine = () => {
    if (!selectedBlock || (selectedBlock.lines || []).length <= 1) return;

    const nextIndex = Math.max(selectedLineIndex - 1, 0);

    updateSelectedBlock((block) => {
      let nextChildren = block.children || [];
      if (
        activeLine?.type === "condition_line" &&
        activeLine.spawned_block_id
      ) {
        nextChildren = nextChildren.filter(
          (child) => child.id !== activeLine.spawned_block_id,
        );
      }

      return {
        ...block,
        lines: (block.lines || []).filter((line) => line.id !== activeLine.id),
        children: nextChildren,
      };
    });

    setLineWizardIndex((prev) => ({ ...prev, [selectedBlock.id]: nextIndex }));
  };

  const removePreviewLine = (blockId, lineId) => {
    if (!selectedEntry) return;

    const targetBlock = findBlock(blockTree, blockId);
    if (!targetBlock || (targetBlock.lines || []).length <= 1) return;

    const removedLine =
      (targetBlock.lines || []).find((line) => line.id === lineId) || null;
    const removedIndex = (targetBlock.lines || []).findIndex(
      (line) => line.id === lineId,
    );

    updateEntryBlocks(selectedEntry.id, (blocks) =>
      mapBlocks(blocks, (block) => {
        if (block.id !== blockId) return block;

        let nextChildren = block.children || [];
        if (
          removedLine?.type === "condition_line" &&
          removedLine.spawned_block_id
        ) {
          nextChildren = nextChildren.filter(
            (child) => child.id !== removedLine.spawned_block_id,
          );
        }

        return {
          ...block,
          lines: (block.lines || []).filter((line) => line.id !== lineId),
          children: nextChildren,
        };
      }),
    );

    if (selectedBlockId === blockId) {
      const nextIndex = Math.max(
        0,
        Math.min(removedIndex, (targetBlock.lines || []).length - 2),
      );
      setLineWizardIndex((prev) => ({ ...prev, [blockId]: nextIndex }));
    }
  };

  const handleResetEntryForm = useCallback(() => {
    if (!selectedEntry) return;

    const confirmReset = window.confirm(
      "Are you sure you want to reset this form?",
    );
    if (!confirmReset) return;

    if (selectedEntry.isDraft) {
      const resetDraftEntry = {
        ...selectedEntry,
        note: "",
        blocks: [createBlock("")],
      };

      setEntries((prev) =>
        prev.map((entry) =>
          entry.id === selectedEntry.id ? resetDraftEntry : entry,
        ),
      );

      const firstBlockId = resetDraftEntry.blocks?.[0]?.id || null;
      setSelectedBlockId(firstBlockId);
      if (firstBlockId) {
        setLineWizardIndex((prev) => ({ ...prev, [firstBlockId]: 0 }));
      }
      return;
    }

    const initialEntry = entryInitialStateRef.current[selectedEntry.id];
    if (!initialEntry) return;

    const restoredEntry = cloneEntryData(initialEntry);
    setEntries((prev) =>
      prev.map((entry) =>
        entry.id === selectedEntry.id ? restoredEntry : entry,
      ),
    );

    const firstBlockId = restoredEntry.blocks?.[0]?.id || null;
    setSelectedBlockId(firstBlockId);
    if (firstBlockId) {
      setLineWizardIndex((prev) => ({ ...prev, [firstBlockId]: 0 }));
    }
  }, [selectedEntry]);

  const handleCancelEntryEditing = useCallback(() => {
    if (!selectedEntry || selectedEntry.isDraft) return;

    const initialEntry = entryInitialStateRef.current[selectedEntry.id];
    if (initialEntry) {
      const restoredEntry = cloneEntryData(initialEntry);
      setEntries((prev) =>
        prev.map((entry) =>
          entry.id === selectedEntry.id ? restoredEntry : entry,
        ),
      );
    }

    setSelectedEntryId(null);
    setSelectedBlockId(null);
  }, [selectedEntry]);

  const handleSaveEntry = useCallback(() => {
    if (!selectedEntry || !selectedBlock) return;

    const currentEntryId = selectedEntry.id;
    const entryPosition = selectedEntryIndex >= 0 ? selectedEntryIndex + 1 : 1;
    const fallbackTitle = `Entry ${entryPosition}`;

    let nextSelectedEntryId = null;
    let nextSelectedBlockId = null;

    setEntries((prev) => {
      const nextEntries = prev.map((entry, index) => {
        if (entry.id !== currentEntryId) return entry;
        const finalized = {
          ...entry,
          title:
            String(entry.title || `Entry ${index + 1}`).trim() || fallbackTitle,
          isDraft: false,
          blocks: (entry.blocks || []).map((block) => ({
            ...block,
            quantite: normalizeIntegerString(block?.quantite),
          })),
        };
        entryInitialStateRef.current[finalized.id] = cloneEntryData(finalized);
        return finalized;
      });

      const hasDraft = nextEntries.some((entry) => entry.isDraft);
      if (hasDraft) {
        const existingDraft = nextEntries.find((entry) => entry.isDraft);
        nextSelectedEntryId = existingDraft?.id || null;
        nextSelectedBlockId = existingDraft?.blocks?.[0]?.id || null;
        return nextEntries;
      }

      const newDraftEntry = createLocalDraftEntry(nextEntries.length + 1);
      nextSelectedEntryId = newDraftEntry.id;
      nextSelectedBlockId = newDraftEntry.blocks[0].id;
      return [...nextEntries, newDraftEntry];
    });

    if (nextSelectedEntryId && nextSelectedBlockId) {
      setSelectedEntryId(nextSelectedEntryId);
      setSelectedBlockId(nextSelectedBlockId);
      setLineWizardIndex((prev) => ({ ...prev, [nextSelectedBlockId]: 0 }));
    }

    setSavedMessage("Entry saved");
    setTimeout(() => setSavedMessage(""), 1800);
  }, [selectedBlock, selectedEntry, selectedEntryIndex]);

  const applyConditionAction = () => {
    if (!selectedBlock || !activeLine || activeLine.type !== "condition_line")
      return;

    if (activeLine.action_type === "add_dosage_line") {
      const newLine = { ...createDosageLine(), id: createId("line") };
      const nextLineIndex = (selectedBlock.lines || []).length;
      updateSelectedBlock((block) => ({
        ...block,
        lines: [...(block.lines || []), newLine],
      }));
      setSelectedBlockId(selectedBlock.id);
      setLineWizardIndex((prev) => ({
        ...prev,
        [selectedBlock.id]: nextLineIndex,
      }));
      return;
    }

    if (
      ["association", "relay", "create_block"].includes(activeLine.action_type)
    ) {
      if (activeLine.spawned_block_id) {
        setSelectedBlockId(activeLine.spawned_block_id);
        return;
      }

      const childBlock = createBlock(activeLine.action_medication || "");
      updateSelectedBlock((block) => ({
        ...block,
        lines: block.lines.map((line) => {
          if (line.id !== activeLine.id) return line;
          return { ...line, spawned_block_id: childBlock.id };
        }),
        children: [...(block.children || []), childBlock],
      }));

      setSelectedBlockId(childBlock.id);
      setLineWizardIndex((prev) => ({ ...prev, [childBlock.id]: 0 }));
    }
  };

  const selectPreviewLine = (blockId, lineId) => {
    const block = findBlock(blockTree, blockId);
    if (!block) return;

    const lineIdx = (block.lines || []).findIndex((line) => line.id === lineId);
    if (lineIdx < 0) return;

    setSelectedBlockId(blockId);
    setLineWizardIndex((prev) => ({ ...prev, [blockId]: lineIdx }));
  };

  const selectPreviewBlock = (blockId) => {
    const block = findBlock(blockTree, blockId);
    if (!block) return;

    const linesCount = (block.lines || []).length;
    const defaultIndex = linesCount > 0 ? 0 : 0;

    setSelectedBlockId(blockId);
    setLineWizardIndex((prev) => {
      const current = prev[blockId];
      if (Number.isInteger(current) && current >= 0 && current < linesCount)
        return prev;
      return { ...prev, [blockId]: defaultIndex };
    });
  };

  const updateEntryMeta = (entryId, updater) => {
    setEntries((prev) =>
      prev.map((entry) => (entry.id === entryId ? updater(entry) : entry)),
    );
  };

  const validateCurrent = () => {
    if (!selectedBlock) return { messages: [], fields: {} };

    const errors = [];
    const fields = {};

    const addFieldError = (key, message, severity = "error") => {
      fields[key] = { message, severity };
      errors.push(message);
    };

    if (!selectedBlock.medicament?.name?.trim())
      addFieldError("block.medicament", "Medication is required.", "warning");

    const quantityValue = Number(selectedBlock.quantite);
    if (!Number.isInteger(quantityValue) || quantityValue <= 0) {
      addFieldError(
        "block.quantite",
        "Block quantity must be a positive integer.",
      );
    }

    if (!selectedBlock.type_quantite) {
      addFieldError("block.type_quantite", "Quantity type is required.");
    }

    const isValidNumber = (val) => {
      if (val === undefined || val === null) return false;
      if (val === "") return false;
      return !Number.isNaN(Number(val));
    };

    (selectedBlock.lines || []).forEach((line, lineIndex) => {
      if (line.type === "dosage_line") {
        if (!line.dose || Number(line.dose) <= 0)
          addFieldError(
            `${line.id}.dose`,
            `Line ${lineIndex + 1}: dose must be positive.`,
          );
        if (!line.unite)
          addFieldError(
            `${line.id}.unite`,
            `Line ${lineIndex + 1}: unit is required.`,
          );
        if (!line.frequence)
          addFieldError(
            `${line.id}.frequence`,
            `Line ${lineIndex + 1}: frequency is required.`,
          );
        if (!line.voie)
          addFieldError(
            `${line.id}.voie`,
            `Line ${lineIndex + 1}: route is required.`,
          );
        if (!line.moment)
          addFieldError(
            `${line.id}.moment`,
            `Line ${lineIndex + 1}: moment is required.`,
          );
        if (!line.repas)
          addFieldError(
            `${line.id}.repas`,
            `Line ${lineIndex + 1}: meal timing is required.`,
          );
        if (!line.duree_unite)
          addFieldError(
            `${line.id}.duree_unite`,
            `Line ${lineIndex + 1}: duration unit is required.`,
          );
        if (!line.renouvellement)
          addFieldError(
            `${line.id}.renouvellement`,
            `Line ${lineIndex + 1}: renewal is required.`,
          );

        if (line.duree_unite === "calendar") {
          const startOk = isValidNumber(line.duree_calendar_start);
          const endOk = isValidNumber(line.duree_calendar_end);

          if (!startOk || !endOk) {
            addFieldError(
              `${line.id}.duree_calendar_start`,
              `Line ${lineIndex + 1}: calendar start/end must be numbers.`,
            );
            addFieldError(
              `${line.id}.duree_calendar_end`,
              `Line ${lineIndex + 1}: calendar start/end must be numbers.`,
            );
          }

          if (
            startOk &&
            endOk &&
            Number(line.duree_calendar_end) <= Number(line.duree_calendar_start)
          ) {
            addFieldError(
              `${line.id}.duree_calendar_end`,
              `Line ${lineIndex + 1}: calendar end must be greater than start.`,
            );
          }
        }
      }

      if (line.type === "condition_line" && !line.expression.trim()) {
        const allowUnconditional = [
          "association",
          "relay",
          "create_block",
        ].includes(line.action_type);
        if (!allowUnconditional) {
          addFieldError(
            `${line.id}.expression`,
            `Line ${lineIndex + 1}: IF expression is required.`,
          );
        }
      }

      if (line.type === "step_line") {
        const source = {
          dose: line.dose,
          unite: line.unite,
          frequence: line.frequence,
          voie: line.voie,
          moment: line.moment,
          repas: line.repas,
          duree_unite: line.duree_unite,
          duree_calendar_start: line.duree_calendar_start,
          duree_calendar_end: line.duree_calendar_end,
          renouvellement: line.renouvellement,
        };

        if (!isValidNumber(source.dose) || Number(source.dose) <= 0)
          addFieldError(
            `${line.id}.dose`,
            `Step line ${lineIndex + 1}: dose must be positive.`,
          );
        if (!source.unite)
          addFieldError(
            `${line.id}.unite`,
            `Step line ${lineIndex + 1}: unit is required.`,
          );
        if (!source.frequence)
          addFieldError(
            `${line.id}.frequence`,
            `Step line ${lineIndex + 1}: frequency is required.`,
          );
        if (!source.voie)
          addFieldError(
            `${line.id}.voie`,
            `Step line ${lineIndex + 1}: route is required.`,
          );
        if (!source.moment)
          addFieldError(
            `${line.id}.moment`,
            `Step line ${lineIndex + 1}: moment is required.`,
          );
        if (!source.repas)
          addFieldError(
            `${line.id}.repas`,
            `Step line ${lineIndex + 1}: meal timing is required.`,
          );
        if (!source.duree_unite)
          addFieldError(
            `${line.id}.duree_unite`,
            `Step line ${lineIndex + 1}: duration unit is required.`,
          );
        if (!source.renouvellement)
          addFieldError(
            `${line.id}.renouvellement`,
            `Step line ${lineIndex + 1}: renewal is required.`,
          );
      }

      if (line.type === "test_line" && !(line.tests || []).length) {
        addFieldError(
          `${line.id}.tests`,
          `Line ${lineIndex + 1}: select at least one test.`,
        );
      }
    });

    return { messages: errors, fields };
  };

  const fieldErrors = validateCurrent().fields;

  return (
    <div
      className="NewPrescriptorTab np-panel"
      style={{ padding: 0, border: "none" }}
    >
      <PrescriptionForm
        selectedBlock={selectedBlock}
        blockTree={blockTree}
        previewEntryNote={selectedEntry?.note}
        selectedBlockId={selectedBlockId}
        selectedLineId={activeLine?.id}
        onSelectPreviewLine={selectPreviewLine}
        onSelectPreviewBlock={selectPreviewBlock}
        onSaveEntry={handleSaveEntry}
        onResetEntryForm={handleResetEntryForm}
        onCancelEditing={handleCancelEntryEditing}
        selectedEntry={selectedEntry}
        onUpdateEntry={(updater) => {
          if (!selectedEntry) return;
          updateEntryMeta(selectedEntry.id, updater);
        }}
        onShowJson={() => {}}
        showAdvanced={showAdvanced}
        setShowAdvanced={setShowAdvanced}
        selectedLineIndex={selectedLineIndex}
        setLineWizardIndex={setLineWizardIndex}
        addLine={addLine}
        removeCurrentLine={removeCurrentLine}
        updateSelectedBlock={updateSelectedBlock}
        updateLine={updateLine}
        activeLine={activeLine}
        lineLabel={lineLabel}
        applyConditionAction={applyConditionAction}
        onDeletePreviewLine={removePreviewLine}
        createId={createId}
        medicationOptions={medicationOptions}
        onMedicationQuery={handleMedicationQuery}
        testGroups={testGroups}
        UNIT_OPTIONS={UNIT_OPTIONS}
        DOSE_OPTIONS={DOSE_OPTIONS}
        ROUTE_OPTIONS={ROUTE_OPTIONS}
        FREQUENCY_OPTIONS={FREQUENCY_OPTIONS}
        MOMENT_OPTIONS={MOMENT_OPTIONS}
        MEAL_OPTIONS={MEAL_OPTIONS}
        DURATION_OPTIONS={DURATION_OPTIONS}
        CONDITION_OPTIONS={CONDITION_OPTIONS}
        QTY_OPTIONS={QTY_OPTIONS}
        RENEWAL_OPTIONS={RENEWAL_OPTIONS}
        CONDITION_ACTION_OPTIONS={CONDITION_ACTION_OPTIONS}
        fieldErrors={fieldErrors}
        savedMessage={savedMessage}
        duplicateWarnings={duplicateWarnings}
        isSavingEntry={false}
      />

      <PrescriptorEntriesList
        entries={entries}
        onAddEntry={addEntry}
        onSelectEntry={selectEntry}
        onDeleteEntry={deleteEntry}
        deletingEntryId=""
        canDeleteEntry={() => true}
      />
    </div>
  );
};

export default ProtocolPrescriptorComposer;
